import { supabase } from "./supabase";
import type { Oekt } from "./session";

/**
 * Loggene, slik redaktøren leser dem.
 *
 * ÉN SPALTE PER REGISSØR, IKKE PER ØKT. Tre regier ute er tre spalter.
 *
 * Det var økta først, og det var feil. `log_session` er en teknisk enhet:
 * den gjenbrukes for dagen, men en app som startes på nytt, en telefon som
 * byttes eller et skift som deles kan gi flere på samme dag — og da ble den
 * samme personen til tre kolonner ved siden av hverandre, uten at noe sa
 * hvorfor. Redaktøren leser PERSONER, ikke økter. Øktene slås sammen, og
 * postene sorteres på veggklokke på tvers av dem.
 *
 * ANKERET FØLGER POSTEN, ikke spalten. Slår man sammen to økter, kan de ha
 * hvert sitt anker — regi kan ha satt tidskoden på nytt midt på dagen — og
 * da ville ett anker per spalte gitt feil tidskode på halve loggen.
 *
 * REDAKTØREN SKRIVER ALDRI I LOGGEN. Det er ikke en innstilling her, det er
 * at det ikke finnes noen vei: kommentaren ligger i `entry_comment`,
 * markeringen i `entry_mark` og spørsmålet i `su_question`. `log_entry` er
 * append-only og har én forfatter, og det er regi.
 *
 * TIDSKODEN REGNES, DEN LAGRES IKKE. Posten bærer `monotonic_ms`, `boot_id`
 * og `wall_utc`; tidskoden kommer av ankeret ved visning. Det er hele
 * grunnen til at regi kan justere tidskoden i etterkant og få alle poster
 * med seg. Se docs/04-timecode.md.
 *
 * LIVE ER BEST EFFORT. Regi står ofte uten dekning, og da står spalten
 * stille. Flata viser hvor gammelt bildet er framfor å late som det er
 * à jour — se `Loggoekt.sistePost`.
 */

export interface Kommentar {
  readonly id: string;
  readonly tekst: string;
  readonly forfatter: string;
  readonly skrevet: string;
}

export interface Spoersmaal {
  readonly id: string;
  readonly tekst: string;
  readonly deltaker: string | null;
}

export interface Loggpost {
  readonly id: string;
  /** Ankeret DENNE posten ble logget mot. Tidskoden regnes av det. */
  readonly anker: Anker | null;
  /** Knappens etikett, fanget i trykkøyeblikket. Tom for prosa. */
  readonly etikett: string;
  readonly farge: string | null;
  readonly tekst: string;
  readonly wallUtcMs: number;
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly tcUsikker: boolean;
  readonly slettet: boolean;
  /**
   * Forkortelsen til den som skrev posten, naar det ikke er den loggen
   * staar i navnet til.
   *
   * Null for de aller fleste poster: i en logg med én forfatter sier den
   * ingenting, og en forkortelse paa hver linje ville vaert stoey.
   * Teamet deler logg fra 29.10.2026, og DA betyr den noe — redaktoeren
   * maa kunne se at fotografen skrev linja om linsebyttet.
   */
  readonly skrevetAv: string | null;
  readonly kommentarer: readonly Kommentar[];
  readonly markert: boolean;
  readonly spoersmaal: readonly Spoersmaal[];
}

export interface Anker {
  readonly tcFrames: number;
  /**
   * Ankerets egen framerate, ikke produksjonens.
   *
   * De er som regel like, men ankeret er det tidskoden faktisk ble satt mot.
   * Endrer prodleder produksjonens framerate i morgen, skal gårsdagens logg
   * fortsatt regnes med den som gjaldt da.
   */
  readonly framerate: string;
  readonly dropFrame: boolean;
  readonly source: string;
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly wallUtcMs: number;
}

export interface Loggspalte {
  /** Medlemmet. Spalten ER personen. */
  readonly id: string;
  readonly forfatter: string;
  readonly initialer: string;
  readonly lag: string | null;
  /**
   * Regien paa teamet, eller null.
   *
   * ========  HVORFOR IKKE OEKTAS FORFATTER  ========
   *
   * `log_session.author_id` settes til regien av en utloeser paa et
   * team — men bare hvis det FINNES en regi der. Gjoer det ikke det,
   * staar den som aapnet oekta, og det er ofte fotografen som skrev
   * foerste post. Brukeren 02.11.2026: «det kan vaere en foto som
   * skriver foerste loggpost, og ender da opp med aa krediteres til
   * loggen som regi har gjort senere.»
   *
   * Regien slaas derfor opp paa TEAMET, uavhengig av hvem som rakk aa
   * aapne noe. Er det ingen regi der, staar det ingenting — det er et
   * sannere svar enn et navn som tilfeldigvis sto foerst.
   */
  readonly regi: string | null;
  /** Sann så lenge én av personens økter står åpen. */
  readonly aapen: boolean;
  /**
   * Øktene som er slått sammen i spalten.
   *
   * Trengs av skriveindikatoren: feltappen kringkaster ØKT-id, og spalten
   * må kjenne igjen sine egne.
   */
  readonly oektIder: readonly string[];
  readonly poster: readonly Loggpost[];
  /** Veggklokka på siste levende post, eller null. Flata viser alderen. */
  readonly sistePost: number | null;
}

interface Rad { readonly [k: string]: unknown }

const num = (v: unknown): number => Number(v ?? 0);
const str = (v: unknown): string => String(v ?? "");

/**
 * Henter loggene for en opptaksdag, én spalte per regissør.
 *
 * TO SPØRRINGER. Ankrene for seg, fordi posten peker på SITT anker og ikke
 * nødvendigvis på øktas — justerer regi tidskoden midt på dagen, får postene
 * etter justeringen et nytt anker, og da må oppslaget gå på posten.
 *
 * Resten i én: PostgREST henter de innleirede tabellene i samme svar, og
 * alternativet — en runde per post for kommentarer, en for markeringer —
 * ville gitt hundrevis av forespørsler på en travel dag.
 *
 * LUKKEDE ØKTER ER MED. Loggen leses også i etterkant: «hva sto det på dag
 * 7?» er et like gyldig spørsmål som «hva skjer nå».
 */
export async function hentLogger(oekt: Oekt, opptaksdag: string): Promise<Loggspalte[]> {
  if (supabase === null) return [];

  const [ankerSvar, oektSvar] = await Promise.all([
    supabase
      .from("tc_anchor")
      .select("id, tc_frames, framerate, drop_frame, source, monotonic_ms, boot_id, wall_utc")
      .eq("production_id", oekt.production.id)
      .eq("shoot_day", opptaksdag),
    supabase
      .from("log_session")
      .select(
        "id, status, opened_at, author_id, unit_id,"
        + " production_member!log_session_author_id_fkey(display_name, initials),"
        + " unit(name),"
        + " log_entry(id, anchor_id, label, color, text, wall_utc, monotonic_ms, boot_id,"
        + " tc_state, deleted_at, author_id,"
        /* Hvem som skrev DENNE posten. Trengs fordi teamet deler logg:
           uten den kan redaktoeren ikke se at fotografen skrev linja om
           linsebyttet og regi linja over. Se 20261029090000. */
        + " forfatter:production_member!log_entry_author_id_fkey(initials),"
        + " entry_comment(id, text, created_at, production_member(display_name)),"
        + " entry_mark(author_id),"
        + " su_question(id, text, participant(abbreviation)))",
      )
      .eq("production_id", oekt.production.id)
      .eq("shoot_day", opptaksdag)
      .order("opened_at", { ascending: true }),
  ]);

  if (oektSvar.error !== null || oektSvar.data === null) return [];

  const ankere = new Map<string, Anker>();
  for (const r of (ankerSvar.data ?? []) as unknown as Rad[]) {
    ankere.set(str(r["id"]), {
      tcFrames: num(r["tc_frames"]),
      framerate: str(r["framerate"]),
      dropFrame: r["drop_frame"] === true,
      source: str(r["source"]),
      monotonicMs: num(r["monotonic_ms"]),
      bootId: str(r["boot_id"]),
      wallUtcMs: Date.parse(str(r["wall_utc"])),
    });
  }

  /* Samles per MEDLEM. Rekkefølgen mellom spaltene følger første økt, slik
     at de ikke bytter plass mens redaktøren leser. */
  /*
   * Regien per team. Ett oppslag, ikke ett per spalte.
   *
   * Samme proeve som utloeseren bruker — rolle `regi`, aktiv i dag — slik
   * at flata og basen svarer det samme. Se 20261029100000.
   */
  const regiPerLag = new Map<string, string>();
  {
    const i_dag = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Oslo" });
    const { data: regier } = await supabase
      .from("production_member")
      .select("unit_id, display_name, active_from, active_to")
      .eq("production_id", oekt.production.id)
      .eq("role", "regi")
      .not("unit_id", "is", null);
    for (const r of (regier ?? []) as unknown as Rad[]) {
      const fra = r["active_from"] == null ? null : str(r["active_from"]);
      const til = r["active_to"] == null ? null : str(r["active_to"]);
      if (fra !== null && fra > i_dag) continue;
      if (til !== null && til < i_dag) continue;
      const u = str(r["unit_id"]);
      if (!regiPerLag.has(u)) regiPerLag.set(u, str(r["display_name"]));
    }
  }

  const spalter = new Map<string, {
    forfatter: string; initialer: string; lag: string | null;
    regi: string | null;
    aapen: boolean; oektIder: string[]; poster: Loggpost[];
  }>();

  for (const rad of oektSvar.data as unknown as Rad[]) {
    const medlem = (rad["production_member"] ?? {}) as Rad;
    const lag = (rad["unit"] ?? null) as Rad | null;
    const forfatterId = str(rad["author_id"]);

    let spalte = spalter.get(forfatterId);
    if (spalte === undefined) {
      spalte = {
        forfatter: str(medlem["display_name"]),
        initialer: str(medlem["initials"]),
        lag: lag === null ? null : str(lag["name"]),
        regi: rad["unit_id"] == null
          ? null : regiPerLag.get(str(rad["unit_id"])) ?? null,
        aapen: false,
        oektIder: [],
        poster: [],
      };
      spalter.set(forfatterId, spalte);
    }
    spalte.oektIder.push(str(rad["id"]));
    if (str(rad["status"]) === "open") spalte.aapen = true;
    /* Teamet tas fra den SISTE økta som har ett. Bytter regi fra LET 2 til
       LET 3 midt på dagen, er det LET 3 hen står i nå. */
    if (lag !== null) {
      spalte.lag = str(lag["name"]);
      spalte.regi = regiPerLag.get(str(rad["unit_id"])) ?? null;
    }

    for (const p of (rad["log_entry"] ?? []) as Rad[]) {
      spalte.poster.push({
        id: str(p["id"]),
        anker: ankere.get(str(p["anchor_id"])) ?? null,
        etikett: str(p["label"]),
        farge: p["color"] === null || p["color"] === undefined ? null : str(p["color"]),
        tekst: str(p["text"]),
        wallUtcMs: Date.parse(str(p["wall_utc"])),
        monotonicMs: num(p["monotonic_ms"]),
        bootId: str(p["boot_id"]),
        skrevetAv: str(p["author_id"]) === forfatterId
          ? null
          : str(((p["forfatter"] ?? {}) as Rad)["initials"]) || null,
        tcUsikker: str(p["tc_state"]) === "usikker",
        slettet: p["deleted_at"] !== null && p["deleted_at"] !== undefined,
        kommentarer: ((p["entry_comment"] ?? []) as Rad[])
          .sort((x, y) => Date.parse(str(x["created_at"])) - Date.parse(str(y["created_at"])))
          .map((k): Kommentar => ({
            id: str(k["id"]),
            tekst: str(k["text"]),
            forfatter: str(((k["production_member"] ?? {}) as Rad)["display_name"]),
            skrevet: str(k["created_at"]),
          })),
        markert: ((p["entry_mark"] ?? []) as Rad[]).length > 0,
        spoersmaal: ((p["su_question"] ?? []) as Rad[]).map((q): Spoersmaal => ({
          id: str(q["id"]),
          tekst: str(q["text"]),
          deltaker: q["participant"] === null || q["participant"] === undefined
            ? null
            : str((q["participant"] as Rad)["abbreviation"]),
        })),
      });
    }
  }

  return [...spalter].map(([id, s]) => {
    /* Sorteres på VEGGKLOKKE, aldri på tidskode: en tidskode som ruller over
       midnatt ville stokket dagen. Og det er her to økter blir én logg. */
    const poster = s.poster.slice().sort((x, y) => x.wallUtcMs - y.wallUtcMs);
    const levende = poster.filter((p) => !p.slettet);
    return {
      id,
      forfatter: s.forfatter,
      initialer: s.initialer,
      lag: s.lag,
      regi: s.regi,
      aapen: s.aapen,
      oektIder: s.oektIder,
      poster,
      sistePost: levende.length === 0 ? null : levende[levende.length - 1]!.wallUtcMs,
    };
  });
}

/* ---- det redaktøren skriver --------------------------------------------
 *
 * Alle tre går i EGNE tabeller. Ingen av dem rører `log_entry`.
 */

export async function skrivKommentar(
  postId: string, medlemId: string, tekst: string,
): Promise<boolean> {
  if (supabase === null || tekst.trim() === "") return false;
  const { error } = await supabase
    .from("entry_comment")
    .insert({ log_entry_id: postId, author_id: medlemId, text: tekst.trim() });
  return error === null;
}

export async function settMarkering(
  postId: string, medlemId: string, paa: boolean,
): Promise<boolean> {
  if (supabase === null) return false;
  if (paa) {
    const { error } = await supabase
      .from("entry_mark")
      .upsert({ log_entry_id: postId, author_id: medlemId },
        { onConflict: "log_entry_id,author_id" });
    return error === null;
  }
  const { error } = await supabase
    .from("entry_mark")
    .delete()
    .eq("log_entry_id", postId)
    .eq("author_id", medlemId);
  return error === null;
}

/**
 * Et synkspørsmål skrevet ut av en loggpost.
 *
 * Det lever videre i SU-/synklista som alle andre spørsmål — det er DER det
 * skal stilles, og det blir stående til regi huker det av. `source_entry_id`
 * er bare opphavet, så regi ser på posten hvor spørsmålet kom fra.
 *
 * `sort_order` legges bakerst: et spørsmål som kommer inn midt i en dag skal
 * ikke dytte seg foran dem som alt står der.
 */
export async function skrivSpoersmaal(
  produksjonId: string, postId: string, deltakerId: string | null, tekst: string,
): Promise<boolean> {
  if (supabase === null || tekst.trim() === "") return false;
  const { data } = await supabase
    .from("su_question")
    .select("sort_order")
    .eq("production_id", produksjonId)
    .order("sort_order", { ascending: false })
    .limit(1)
    .maybeSingle();
  const neste = (data === null ? 0 : Number(data.sort_order)) + 1;

  const { error } = await supabase.from("su_question").insert({
    production_id: produksjonId,
    participant_id: deltakerId,
    text: tekst.trim(),
    sort_order: neste,
    source_entry_id: postId,
  });
  return error === null;
}
